import React, { useState, useEffect } from "react";
import { makeRequest2 } from "../../../../../utils/makeRequest";

const WalletMetadataModal = ({ show, onClose, wallet, facilityId }) => {
  const [loading, setLoading] = useState(false);
  const [metadata, setMetadata] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    pages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [summary, setSummary] = useState({
    totalRecords: 0,
    totalAmount: 0,
    totalToLandlord: 0,
    totalToPropertyManager: 0,
    statusBreakdown: {
      paid: 0,
      partial: 0,
      cancelled: 0,
      pending: 0,
      approved: 0,
      rejected: 0,
    },
  });

  // Filter states
  const [filters, setFilters] = useState({
    status: "",
    dateFrom: "",
    dateTo: "",
    invoiceNumber: "",
  });

  // Available status options
  const statusOptions = [
    { value: "", label: "All Statuses" },
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "cancelled", label: "Cancelled" },
    { value: "paid", label: "Paid" },
    { value: "partial", label: "Partial" },
  ];

  // Calculate summary from metadata when API doesn't provide it
  const calculateSummaryFromMetadata = (metadataArray) => {
    if (!metadataArray || metadataArray.length === 0) {
      return {
        totalRecords: 0,
        totalAmount: 0,
        totalToLandlord: 0,
        totalToPropertyManager: 0,
        statusBreakdown: {
          paid: 0,
          partial: 0,
          cancelled: 0,
          pending: 0,
          approved: 0,
          rejected: 0,
        },
      };
    }

    const statusBreakdown = {
      paid: 0,
      partial: 0,
      cancelled: 0,
      pending: 0,
      approved: 0,
      rejected: 0,
    };

    let totalAmount = 0;
    let totalToLandlord = 0;
    let totalToPropertyManager = 0;

    metadataArray.forEach((item) => {
      // Count by status
      const status = item.status || "unknown";
      if (statusBreakdown.hasOwnProperty(status)) {
        statusBreakdown[status]++;
      }

      // Sum amounts
      totalAmount += item.amount || 0;
      totalToLandlord += item.amountToLandlord || 0;
      totalToPropertyManager += item.amountToPropertyManager || 0;
    });

    return {
      totalRecords: metadataArray.length,
      totalAmount,
      totalToLandlord,
      totalToPropertyManager,
      statusBreakdown,
    };
  };

  // Filter metadata based on current filters
  const getFilteredMetadata = () => {
    let filtered = [...metadata];

    // Status filter
    if (filters.status) {
      filtered = filtered.filter((item) => item.status === filters.status);
    }

    // Date range filter
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      filtered = filtered.filter((item) => {
        const itemDate = new Date(item.createdAt);
        return itemDate >= fromDate;
      });
    }

    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999); // End of day
      filtered = filtered.filter((item) => {
        const itemDate = new Date(item.createdAt);
        return itemDate <= toDate;
      });
    }

    // Invoice number filter
    if (filters.invoiceNumber) {
      filtered = filtered.filter(
        (item) =>
          item.invoiceNumber
            ?.toLowerCase()
            .includes(filters.invoiceNumber.toLowerCase()) ||
          item.invoiceId
            ?.toLowerCase()
            .includes(filters.invoiceNumber.toLowerCase()),
      );
    }

    return filtered;
  };

  const filteredMetadata = getFilteredMetadata();

  // Calculate filtered summary
  const filteredSummary = calculateSummaryFromMetadata(filteredMetadata);

  // Load wallet metadata
  const loadWalletMetadata = async (page = 1) => {
    if (!wallet || !facilityId) return;

    try {
      setLoading(true);

      const metadataUrl = `/api/app/wallet/${facilityId}/wallet/${wallet._id}/metadata?page=${page}&limit=10`;
      console.log("📡 Calling metadata API:", metadataUrl);

      const response = await makeRequest2(metadataUrl, "GET");
      console.log("📥 Metadata API response:", response);

      if (
        response.success ||
        response.message === "Wallet metadata retrieved successfully"
      ) {
        const metadataData = response.data?.metadata || response.metadata || [];
        const paginationData = response.data?.pagination ||
          response.pagination || {
            total: 0,
            pages: 1,
            hasNextPage: false,
            hasPrevPage: false,
          };
        const summaryData = response.data?.summary ||
          response.summary || {
            totalRecords: 0,
            totalAmount: 0,
            totalToLandlord: 0,
            totalToPropertyManager: 0,
            statusBreakdown: {
              paid: 0,
              partial: 0,
              cancelled: 0,
              pending: 0,
              approved: 0,
              rejected: 0,
            },
          };

        if (page === 1) {
          setMetadata(metadataData);
        } else {
          setMetadata((prev) => [...prev, ...metadataData]);
        }

        setPagination(paginationData);

        // Calculate summary from actual metadata if API doesn't provide it
        if (summaryData.totalRecords === 0 && metadataData.length > 0) {
          const calculatedSummary = calculateSummaryFromMetadata(metadataData);
          setSummary(calculatedSummary);
        } else {
          setSummary(summaryData);
        }

        console.log("✅ Metadata loaded:", metadataData.length, "records");
      } else {
        console.log("❌ No metadata found or error:", response);
        setMetadata([]);
        setPagination({
          total: 0,
          pages: 1,
          hasNextPage: false,
          hasPrevPage: false,
        });
        setSummary({
          totalRecords: 0,
          totalAmount: 0,
          totalToLandlord: 0,
          totalToPropertyManager: 0,
          statusBreakdown: {
            paid: 0,
            partial: 0,
            cancelled: 0,
            pending: 0,
            approved: 0,
            rejected: 0,
          },
        });
      }
    } catch (error) {
      console.error("❌ Error loading metadata:", error);
      setMetadata([]);
      setSummary({
        totalRecords: 0,
        totalAmount: 0,
        totalToLandlord: 0,
        totalToPropertyManager: 0,
        statusBreakdown: {
          paid: 0,
          partial: 0,
          cancelled: 0,
          pending: 0,
          approved: 0,
          rejected: 0,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (filterKey, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterKey]: value,
    }));
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      status: "",
      dateFrom: "",
      dateTo: "",
      invoiceNumber: "",
    });
  };

  // Update summary when metadata changes
  useEffect(() => {
    if (metadata.length > 0) {
      const calculatedSummary = calculateSummaryFromMetadata(metadata);
      console.log("📊 Calculated summary from metadata:", calculatedSummary);
      setSummary(calculatedSummary);
    }
  }, [metadata]);

  // Load metadata when modal opens or wallet changes
  useEffect(() => {
    if (show && wallet && facilityId) {
      setCurrentPage(1);
      setFilters({
        status: "",
        dateFrom: "",
        dateTo: "",
        invoiceNumber: "",
      });
      loadWalletMetadata(1);
    }
  }, [show, wallet, facilityId]);

  // Handle load more
  const handleLoadMore = async () => {
    const nextPage = currentPage + 1;
    await loadWalletMetadata(nextPage);
    setCurrentPage(nextPage);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  // Fixed formatDate function with proper error handling
  const formatDate = (dateString) => {
    // Handle null, undefined, or empty values
    if (!dateString) {
      return "N/A";
    }

    // Create date object
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn("Invalid date string:", dateString);
      return "Invalid Date";
    }

    // Return formatted date
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Custom status badge styles function
  const getStatusBadgeStyle = (status) => {
    const baseStyle = {
      padding: "4px 8px",
      borderRadius: "4px",
      fontSize: "12px",
      fontWeight: "bold",
      display: "inline-block",
      textAlign: "center",
      minWidth: "60px",
    };

    switch (status) {
      case "paid":
        return {
          ...baseStyle,
          backgroundColor: "#d4edda", // Light green
          color: "#155724", // Dark green
          border: "1px solid #c3e6cb",
        };
      case "approved":
        return {
          ...baseStyle,
          backgroundColor: "#d1ecf1", // Light blue
          color: "#0c5460", // Dark blue
          border: "1px solid #bee5eb",
        };
      case "pending":
        return {
          ...baseStyle,
          backgroundColor: "#fff3cd", // Light yellow
          color: "#856404", // Dark yellow/brown
          border: "1px solid #ffeaa7",
        };
      case "partial":
        return {
          ...baseStyle,
          backgroundColor: "#e2e3e5", // Light gray
          color: "#383d41", // Dark gray
          border: "1px solid #d6d8db",
        };
      case "rejected":
        return {
          ...baseStyle,
          backgroundColor: "#f8d7da", // Light red
          color: "#721c24", // Dark red
          border: "1px solid #f5c6cb",
        };
      case "cancelled":
        return {
          ...baseStyle,
          backgroundColor: "#f8f9fa", // Very light gray
          color: "#495057", // Dark gray
          border: "1px solid #dee2e6",
        };
      default:
        return {
          ...baseStyle,
          backgroundColor: "#e2e3e5", // Light gray
          color: "#383d41", // Dark gray
          border: "1px solid #d6d8db",
        };
    }
  };

  // Custom status badge styles for larger badges (status breakdown section)
  const getStatusBadgeStyleLarge = (status) => {
    const baseStyle = {
      padding: "6px 12px",
      borderRadius: "6px",
      fontSize: "14px",
      fontWeight: "bold",
      display: "inline-block",
      textAlign: "center",
      minWidth: "80px",
      cursor: "pointer",
      transition: "all 0.2s ease",
    };

    switch (status) {
      case "paid":
        return {
          ...baseStyle,
          backgroundColor: "#d4edda",
          color: "#155724",
          border: "1px solid #c3e6cb",
        };
      case "approved":
        return {
          ...baseStyle,
          backgroundColor: "#d1ecf1",
          color: "#0c5460",
          border: "1px solid #bee5eb",
        };
      case "pending":
        return {
          ...baseStyle,
          backgroundColor: "#fff3cd",
          color: "#856404",
          border: "1px solid #ffeaa7",
        };
      case "partial":
        return {
          ...baseStyle,
          backgroundColor: "#e2e3e5",
          color: "#383d41",
          border: "1px solid #d6d8db",
        };
      case "rejected":
        return {
          ...baseStyle,
          backgroundColor: "#f8d7da",
          color: "#721c24",
          border: "1px solid #f5c6cb",
        };
      case "cancelled":
        return {
          ...baseStyle,
          backgroundColor: "#f8f9fa",
          color: "#495057",
          border: "1px solid #dee2e6",
        };
      default:
        return {
          ...baseStyle,
          backgroundColor: "#e2e3e5",
          color: "#383d41",
          border: "1px solid #d6d8db",
        };
    }
  };

  // Handle status badge click for quick filtering
  const handleStatusBadgeClick = (status) => {
    setFilters((prev) => ({
      ...prev,
      status: prev.status === status ? "" : status,
    }));
  };

  if (!show || !wallet) return null;

  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header d-flex justify-content-between align-items-center py-3 px-4">
            <h5 className="modal-title">
              <i className="fa fa-file-invoice-dollar mr-2"></i>
              Wallet Metadata - {wallet.walletType || "General"} Wallet
            </h5>
            <button
              type="button"
              className="btn btn-light btn-sm rounded-circle p-2"
              onClick={onClose}
              style={{
                width: "40px",
                height: "40px",
                border: "1px solid #dee2e6",
                fontSize: "18px",
                lineHeight: "1",
              }}
            >
              <span className="text-muted">&times;</span>
            </button>
          </div>

          <div className="modal-body">
            {/* Wallet Info */}
            <div className="card mb-3">
              <div className="card-body">
                <div className="row">
                  <div className="col-md-3">
                    <h6>Wallet Type</h6>
                    <span
                      className="font-weight-bold text-primary"
                      style={{
                        backgroundColor: "#e3f2fd",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "14px",
                      }}
                    >
                      {wallet.walletType || "General"}
                    </span>
                  </div>
                  <div className="col-md-3">
                    <h6>Owner Type</h6>
                    <span
                      className="font-weight-bold text-info"
                      style={{
                        backgroundColor: "#e0f2f1",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "14px",
                      }}
                    >
                      {wallet.ownerType}
                    </span>
                  </div>
                  <div className="col-md-3">
                    <h6>Current Balance</h6>
                    <h4 className="text-success mb-0">
                      {formatCurrency(wallet.amount)}
                    </h4>
                  </div>
                  <div className="col-md-3">
                    <h6>Total Metadata Records</h6>
                    <h4 className="text-primary mb-0">
                      {summary.totalRecords}
                    </h4>
                  </div>
                </div>
              </div>
            </div>

            {/* Filters Section */}
            {summary.totalRecords > 0 && (
              <div className="card mb-4">
                <div className="card-header">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">
                      <i className="fa fa-filter mr-2"></i>
                      Filters
                    </h6>
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handleResetFilters}
                    >
                      <i className="fa fa-undo mr-1"></i>
                      Reset Filters
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-3">
                      <div className="form-group">
                        <label>Status</label>
                        <select
                          className="form-control"
                          value={filters.status}
                          onChange={(e) =>
                            handleFilterChange("status", e.target.value)
                          }
                        >
                          {statusOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-group">
                        <label>From Date</label>
                        <input
                          type="date"
                          className="form-control"
                          value={filters.dateFrom}
                          onChange={(e) =>
                            handleFilterChange("dateFrom", e.target.value)
                          }
                        />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-group">
                        <label>To Date</label>
                        <input
                          type="date"
                          className="form-control"
                          value={filters.dateTo}
                          onChange={(e) =>
                            handleFilterChange("dateTo", e.target.value)
                          }
                        />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-group">
                        <label>Invoice Search</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Invoice number or ID..."
                          value={filters.invoiceNumber}
                          onChange={(e) =>
                            handleFilterChange("invoiceNumber", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>

                  {/* Filter Results Info */}
                  <div className="mt-2">
                    <small className="text-muted">
                      Showing {filteredMetadata.length} of {metadata.length}{" "}
                      transactions
                      {filters.status &&
                        ` • Status: ${statusOptions.find((opt) => opt.value === filters.status)?.label}`}
                      {filters.dateFrom && ` • From: ${filters.dateFrom}`}
                      {filters.dateTo && ` • To: ${filters.dateTo}`}
                      {filters.invoiceNumber &&
                        ` • Search: "${filters.invoiceNumber}"`}
                    </small>
                  </div>
                </div>
              </div>
            )}

            {/* Summary Cards - Show filtered summary when filters are active */}
            {summary.totalRecords > 0 && (
              <div className="row mb-3">
                <div className="col-md-4">
                  <div className="card border-success">
                    <div className="card-body text-center">
                      <h6 className="text-success">
                        {filteredMetadata.length < metadata.length
                          ? "Filtered"
                          : "Total"}{" "}
                        Amount
                      </h6>
                      <h4 className="text-success mb-0">
                        {formatCurrency(filteredSummary.totalAmount)}
                      </h4>
                      {filteredMetadata.length < metadata.length && (
                        <small className="text-muted">
                          of {formatCurrency(summary.totalAmount)}
                        </small>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card border-info">
                    <div className="card-body text-center">
                      <h6 className="text-info">To Landlord</h6>
                      <h4 className="text-info mb-0">
                        {formatCurrency(filteredSummary.totalToLandlord)}
                      </h4>
                      {filteredMetadata.length < metadata.length && (
                        <small className="text-muted">
                          of {formatCurrency(summary.totalToLandlord)}
                        </small>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card border-warning">
                    <div className="card-body text-center">
                      <h6 className="text-warning">To Property Manager</h6>
                      <h4 className="text-warning mb-0">
                        {formatCurrency(filteredSummary.totalToPropertyManager)}
                      </h4>
                      {filteredMetadata.length < metadata.length && (
                        <small className="text-muted">
                          of {formatCurrency(summary.totalToPropertyManager)}
                        </small>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Enhanced Status Breakdown with Custom Styles and Click Filtering */}
            {summary.totalRecords > 0 && (
              <div className="card mb-3">
                <div className="card-header">
                  <h6 className="mb-0">
                    Transaction Status Breakdown
                    <small className="text-muted ml-2">(Click to filter)</small>
                  </h6>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-2 text-center">
                      <div className="mb-2">
                        <span
                          style={{
                            ...getStatusBadgeStyleLarge("pending"),
                            opacity:
                              filters.status && filters.status !== "pending"
                                ? 0.5
                                : 1,
                            transform:
                              filters.status === "pending"
                                ? "scale(1.05)"
                                : "scale(1)",
                          }}
                          onClick={() => handleStatusBadgeClick("pending")}
                          title="Click to filter by pending status"
                        >
                          Pending: {summary.statusBreakdown.pending || 0}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-2 text-center">
                      <div className="mb-2">
                        <span
                          style={{
                            ...getStatusBadgeStyleLarge("approved"),
                            opacity:
                              filters.status && filters.status !== "approved"
                                ? 0.5
                                : 1,
                            transform:
                              filters.status === "approved"
                                ? "scale(1.05)"
                                : "scale(1)",
                          }}
                          onClick={() => handleStatusBadgeClick("approved")}
                          title="Click to filter by approved status"
                        >
                          Approved: {summary.statusBreakdown.approved || 0}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-2 text-center">
                      <div className="mb-2">
                        <span
                          style={{
                            ...getStatusBadgeStyleLarge("paid"),
                            opacity:
                              filters.status && filters.status !== "paid"
                                ? 0.5
                                : 1,
                            transform:
                              filters.status === "paid"
                                ? "scale(1.05)"
                                : "scale(1)",
                          }}
                          onClick={() => handleStatusBadgeClick("paid")}
                          title="Click to filter by paid status"
                        >
                          Paid: {summary.statusBreakdown.paid || 0}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-2 text-center">
                      <div className="mb-2">
                        <span
                          style={{
                            ...getStatusBadgeStyleLarge("partial"),
                            opacity:
                              filters.status && filters.status !== "partial"
                                ? 0.5
                                : 1,
                            transform:
                              filters.status === "partial"
                                ? "scale(1.05)"
                                : "scale(1)",
                          }}
                          onClick={() => handleStatusBadgeClick("partial")}
                          title="Click to filter by partial status"
                        >
                          Partial: {summary.statusBreakdown.partial || 0}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-2 text-center">
                      <div className="mb-2">
                        <span
                          style={{
                            ...getStatusBadgeStyleLarge("rejected"),
                            opacity:
                              filters.status && filters.status !== "rejected"
                                ? 0.5
                                : 1,
                            transform:
                              filters.status === "rejected"
                                ? "scale(1.05)"
                                : "scale(1)",
                          }}
                          onClick={() => handleStatusBadgeClick("rejected")}
                          title="Click to filter by rejected status"
                        >
                          Rejected: {summary.statusBreakdown.rejected || 0}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-2 text-center">
                      <div className="mb-2">
                        <span
                          style={{
                            ...getStatusBadgeStyleLarge("cancelled"),
                            opacity:
                              filters.status && filters.status !== "cancelled"
                                ? 0.5
                                : 1,
                            transform:
                              filters.status === "cancelled"
                                ? "scale(1.05)"
                                : "scale(1)",
                          }}
                          onClick={() => handleStatusBadgeClick("cancelled")}
                          title="Click to filter by cancelled status"
                        >
                          Cancelled: {summary.statusBreakdown.cancelled || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Metadata List */}
            <div className="card">
              <div className="card-header">
                <h6 className="card-title mb-0">
                  {filters.status
                    ? `${statusOptions.find((opt) => opt.value === filters.status)?.label} `
                    : ""}
                  Invoice Metadata History
                </h6>
              </div>
              <div className="card-body">
                {loading && metadata.length === 0 ? (
                  <div className="text-center py-4">
                    <div className="spinner-border" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <p className="mt-2">Loading metadata...</p>
                  </div>
                ) : filteredMetadata.length === 0 && metadata.length > 0 ? (
                  <div className="text-center py-4">
                    <i className="fa fa-search fa-3x text-muted mb-3"></i>
                    <p className="text-muted">
                      No transactions match your filters
                    </p>
                    <p className="text-sm text-muted">
                      Try adjusting your filters or check different date ranges
                    </p>
                    <button
                      className="btn btn-outline-primary btn-sm mt-2"
                      onClick={handleResetFilters}
                    >
                      <i className="fa fa-undo mr-1"></i>
                      Reset Filters
                    </button>
                  </div>
                ) : metadata.length === 0 ? (
                  <div className="text-center py-4">
                    <i className="fa fa-file-invoice fa-3x text-muted mb-3"></i>
                    <p className="text-muted">No invoice metadata found</p>
                    <p className="text-sm text-muted">
                      Metadata will appear here when invoices are processed for
                      this facility wallet
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped table-hover">
                      <thead>
                        <tr>
                          <th>Invoice</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Landlord Distribution</th>
                          <th>Property Manager Distribution</th>
                          <th>Payment Status</th>
                          <th>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredMetadata.map((item, index) => (
                          <tr key={item._id || index}>
                            <td>
                              <div>
                                <strong>{item.invoiceNumber}</strong>
                                <br />
                                <small className="text-muted font-monospace">
                                  {item.invoiceId}
                                </small>
                              </div>
                            </td>
                            <td>
                              <strong className="text-success">
                                {formatCurrency(item.amount)}
                              </strong>
                            </td>
                            <td>
                              <span style={getStatusBadgeStyle(item.status)}>
                                {item.status}
                              </span>
                            </td>
                            <td>
                              <div>
                                <strong>
                                  {formatCurrency(item.amountToLandlord)}
                                </strong>
                                <br />
                                <span
                                  style={getStatusBadgeStyle(
                                    item.paidToLandlord ? "paid" : "pending",
                                  )}
                                >
                                  {item.paidToLandlord ? "Paid" : "Pending"}
                                </span>
                              </div>
                            </td>
                            <td>
                              <div>
                                <strong>
                                  {formatCurrency(item.amountToPropertyManager)}
                                </strong>
                                <br />
                                <span
                                  style={getStatusBadgeStyle(
                                    item.paidToPropertyManager
                                      ? "paid"
                                      : "pending",
                                  )}
                                >
                                  {item.paidToPropertyManager
                                    ? "Paid"
                                    : "Pending"}
                                </span>
                              </div>
                            </td>
                            <td>
                              <div className="d-flex flex-column">
                                <small className="text-muted">
                                  Landlord: {item.paidToLandlord ? "✅" : "❌"}
                                </small>
                                <small className="text-muted">
                                  PM: {item.paidToPropertyManager ? "✅" : "❌"}
                                </small>
                              </div>
                            </td>
                            <td className="text-sm">
                              {formatDate(item.createdAt)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Load More Button */}
            {!loading && metadata.length > 0 && pagination.hasNextPage && (
              <div className="text-center mt-3">
                <button
                  className="btn btn-outline-primary"
                  onClick={handleLoadMore}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2"></span>
                      Loading...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-chevron-down mr-1"></i>
                      Load More Metadata
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <div className="d-flex justify-content-between align-items-center w-100">
              <small className="text-muted">
                {filteredMetadata.length} of {metadata.length} transaction(s) •
                Filtered Total: {formatCurrency(filteredSummary.totalAmount)}
              </small>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletMetadataModal;
